import React, { createContext, useCallback, useContext, useState, ReactNode } from "react";
import type { StreakMilestonePayload } from "@/lib/streakMilestones";
import { StreakMilestoneCelebration } from "./StreakMilestoneCelebration";

interface StreakCelebrationContextValue {
  /** Enqueue a streak milestone celebration. Won't render until showQueuedCelebration is called. */
  celebrateStreak: (payload: StreakMilestonePayload) => void;
  /** Show the next queued celebration. Call after CheckInCelebration closes. */
  showQueuedCelebration: () => void;
}

const StreakCelebrationContext = createContext<StreakCelebrationContextValue | null>(null);

export function useStreakCelebration() {
  const ctx = useContext(StreakCelebrationContext);
  if (!ctx) throw new Error("useStreakCelebration must be used within StreakCelebrationProvider");
  return ctx;
}

interface Props {
  children: ReactNode;
}

export function StreakCelebrationProvider({ children }: Props) {
  const [queue, setQueue] = useState<StreakMilestonePayload[]>([]);
  const [current, setCurrent] = useState<StreakMilestonePayload | null>(null);

  const celebrateStreak = useCallback((payload: StreakMilestonePayload) => {
    setQueue((q) => [...q, payload]);
  }, []);

  const showQueuedCelebration = useCallback(() => {
    if (current) return;
    setQueue((q) => {
      if (q.length === 0) return q;
      const [next, ...rest] = q;
      setCurrent(next);
      return rest;
    });
  }, [current]);

  const handleComplete = useCallback(() => {
    setQueue((q) => {
      if (q.length === 0) {
        setCurrent(null);
        return q;
      }
      const [next, ...rest] = q;
      setCurrent(next);
      return rest;
    });
  }, []);

  return (
    <StreakCelebrationContext.Provider value={{ celebrateStreak, showQueuedCelebration }}>
      {children}
      {current && (
        <StreakMilestoneCelebration
          key={`${current.streakCount}-${current.title}`}
          payload={current}
          onComplete={handleComplete}
        />
      )}
    </StreakCelebrationContext.Provider>
  );
}
